/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountSelectionModal.scss';

import { unionBy } from 'lodash';
import { array, func } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';
import ActionButton from 'components/shared/Buttons/ActionButton';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import BaseModal from 'components/shared/Modal/BaseModal';
import { arrayOfAccountDescriptors } from 'services/Accounts';
import {
  getOwnedHiddenAccountDescriptors,
  getOwnedAccountDescriptors,
  getRelatedAccounts
} from 'state/account/AccountSelectors';

export const createAccountDescriptorsForSave = (
  ownedAccountDescriptors,
  ownedHiddenAccountDescriptors,
  relatedAccounts,
  selectedRelatedAccountIds
) => {
  const selectedRelatedAccounts = relatedAccounts.filter(
    (account) =>
      selectedRelatedAccountIds &&
      selectedRelatedAccountIds.get(account.accountId)
  );

  const relatedAccountDescriptors = arrayOfAccountDescriptors(
    selectedRelatedAccounts
  );

  return unionBy(ownedHiddenAccountDescriptors, ownedAccountDescriptors, relatedAccountDescriptors, 'accountId');
};

class AccountSelectionModal extends React.Component {
  state = {
    selectedRelatedAccountIds: new Map()
  };

  /** TODO THIS NEEDS TO BE FIXED - This is a bad side-effect pattern where both pathes of address changes
   * need to come through this component in order to execute a save. Problem is, in most cases this shouldn't be
   * doing anything yet we force it to merge an object with things we don't need. This scary in sense we are performing
   * an update merging it with data that has no business being merged.
   */
  componentDidMount() {
    const { relatedAccounts } = this.props;
    if (relatedAccounts.length === 0) {
      this.handleSave();
    }
  }

  handleSave = () => {
    const {
      ownedHiddenAccountDescriptors,
      onSelect,
      ownedAccountDescriptors,
      relatedAccounts
    } = this.props;
    const { selectedRelatedAccountIds } = this.state;

    const accountDescriptorsToSave = createAccountDescriptorsForSave(
      ownedAccountDescriptors,
      ownedHiddenAccountDescriptors,
      relatedAccounts,
      selectedRelatedAccountIds
    );

    onSelect(accountDescriptorsToSave);
  };

  handleClose = () => this.props.onClose();

  handleCheck = (accountId) => (checked) => {
    const { selectedRelatedAccountIds } = this.state;
    selectedRelatedAccountIds.set(accountId, checked);
    this.setState({ selectedRelatedAccountIds });
  };

  handleSelectAll = () => {
    const { relatedAccounts } = this.props;
    const { selectedRelatedAccountIds } = this.state;
    relatedAccounts.forEach((account) =>
      selectedRelatedAccountIds.set(account.accountId, true)
    );
    this.setState({ selectedRelatedAccountIds });
  };

  handleClearAll = () => {
    const { relatedAccounts } = this.props;
    const { selectedRelatedAccountIds } = this.state;
    relatedAccounts.forEach((account) =>
      selectedRelatedAccountIds.set(account.accountId, false)
    );
    this.setState({ selectedRelatedAccountIds });
  };

  render() {
    const { relatedAccounts } = this.props;
    const { selectedRelatedAccountIds } = this.state;

    return (
      <BaseModal
        containerClassName="account-selection-modal"
        disableClickOff={true}
        hideAnchor={true}
        hideClose={true}
        isOpen={true}
        title="Related Accounts">
        <div className="description">
          Please select any related accounts you would also like this address
          change applied to.
        </div>
        <div>
          <a className="action-link" onClick={this.handleSelectAll}>
            Select All
          </a>
          <a className="action-link" onClick={this.handleClearAll}>
            Clear All
          </a>
        </div>

        <div className="related-accounts-list">
          {relatedAccounts.map((account, idx) => (
            <div className="related-account" key={idx}>
              <Checkbox
                className="related-account-checkbox"
                checked={
                  (selectedRelatedAccountIds &&
                    selectedRelatedAccountIds.get(account.accountId)) ||
                  false
                }
                name={account.accountId}
                onChange={this.handleCheck(account.accountId)}
              />
              <AccountDisplay account={account} />
            </div>
          ))}
        </div>

        <div className="buttons">
          <ActionButton onClick={this.handleClose}>Cancel</ActionButton>
          <ActionButton
            className="save-button"
            onClick={this.handleSave}
            type="primary">
            Save
          </ActionButton>
        </div>
      </BaseModal>
    );
  }
}

AccountSelectionModal.propTypes = {
  ownedHiddenAccountDescriptors: array.isRequired,
  onClose: func.isRequired,
  onSelect: func.isRequired,
  ownedAccountDescriptors: array.isRequired,
  relatedAccounts: array.isRequired
};

const mapStateToProps = (state) => ({
  ownedAccountDescriptors: getOwnedAccountDescriptors(state),
  ownedHiddenAccountDescriptors: getOwnedHiddenAccountDescriptors(state),
  relatedAccounts: getRelatedAccounts(state)
});

export default connect(mapStateToProps)(AccountSelectionModal);
